import { useIntl } from 'react-intl';

import { WLIDS } from 'Constants';
import { store } from 'Models/Store';
import { WidthDto } from 'Models/Entities/WidthEntity';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { SelectInput } from 'Views/Components/New/SelectInput';

interface TowOnTypeForWidthInputDto {
	id: string;
	minimumWidthId?: string;
}

interface TrailerWidthInputProps {
	towOnTypes: TowOnTypeForWidthInputDto[];
	trailerTypeId?: string;
	trailerWidthId?: string;
	onChange: (trailerWidthId: string) => void;
	prices?: { [id: string]: number };
	isRequired?: boolean;
	errors?: string;
}

export default function TrailerWidthInput({
	towOnTypes,
	trailerTypeId,
	trailerWidthId,
	onChange,
	prices,
	isRequired,
	errors,
}: TrailerWidthInputProps) {
	const intl = useIntl();
	const label = intl.formatMessage({
		id: 'vehicle-tab__trailer-width__dropdown-label',
		defaultMessage: 'Trailer width',
	});
	const placeholder = intl.formatMessage({ id: WLIDS.width.dropdownPlaceholder, defaultMessage: 'Select width' });

	const options = processWidthOptions({
		trailerTypeId,
		widths: store.widths,
		towOnTypes,
		prices,
	});

	const selectedOption = options.find(o => o.value === trailerWidthId) ?? null;

	return (
		<SelectInput
			className="trailer-width-input"
			label={label}
			placeholder={placeholder}
			options={options}
			value={selectedOption}
			required={isRequired}
			isSearchable={false}
			error={errors}
			onChange={(option: any) => {
				onChange(option?.value ?? '');
			}}
		/>
	);
}

interface ProcessWidthOptionsArgs {
	trailerTypeId?: string;
	widths: WidthDto[];
	towOnTypes: TowOnTypeForWidthInputDto[];
	prices?: { [id: string]: number };
}

const processWidthOptions = ({ trailerTypeId, widths, towOnTypes, prices }: ProcessWidthOptionsArgs) => {
	const baseOptions = widths.map(x => ({ label: x.label, value: x.id, isDisabled: false }));

	if (prices) {
		for (const option of baseOptions) {
			const price = prices[option.value];
			if (price && price !== 0) {
				const priceLabel = formatPriceDisplay(price, false, true, true);
				option.label = `${option.label} (${priceLabel})`;
			}
		}
	}

	const towOnType = towOnTypes.find(v => v.id === trailerTypeId);
	if (!towOnType) {
		return baseOptions;
	}

	const baseWidth = store.widths.find(w => w.isBase);
	const minimumWidth = widths.find(w => w.id === towOnType.minimumWidthId);

	if (!baseWidth || !minimumWidth) {
		return baseOptions;
	}

	const invalidWidthIds = new Set(
		widths.filter(x => x.capacityMultiplier < minimumWidth.capacityMultiplier).map(x => x.id),
	);

	return baseOptions.map(x => ({ ...x, isDisabled: invalidWidthIds.has(x.value) }));
};
